import React, { useMemo } from "react";
import { ProgramChartData } from "../../../../../Controllers/Types/Project/DefaultService/ProgramInterface";
import { Dialog } from "primereact/dialog";
import { Ripple } from "primereact/ripple";
import { Gantt } from "@svar-ui/react-gantt";
import { Locale } from "@svar-ui/react-core";
import "@svar-ui/react-gantt/style.css";
import PageLoader from "../../../../../Components/PageLoader";
import ErrorPage from "../../../../../Components/ErrorPage";

interface ProgramChartModalProps {
    isOpen: boolean;
    view: 'floor' | 'task';
    onClose: () => void;
    programmeChartData?: ProgramChartData | null;
    projectData: any;
    loading: boolean;
    error: string | null;
}

function startOfUKWeek(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);

    const day = x.getDay();
    const diffToMonday = (day + 6) % 7;
    x.setDate(x.getDate() - diffToMonday);
    return x;
}

function startOfNextUKWeek(d: Date): Date {
    const x = startOfUKWeek(d);
    x.setDate(x.getDate() + 7);
    return x;
}

function parseISODate(iso: string | null): Date | null {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
}

function endExclusive(isoEnd: string | null): Date | null {
    const d = parseISODate(isoEnd);
    if (!d) return null;
    const x = new Date(d);
    x.setDate(x.getDate() + 1);
    return x;
}

type GanttTask = {
    id: string | number;
    parent?: string | number;
    text?: string;
    start?: Date;
    end?: Date;
    type?: "task" | "summary" | "milestone" | string;
    barColor?: string;
    chipColor?: string;
    isSummary?: boolean;
};

function ProgramTaskBar({
    data,
}: {
    data: any;
}) {
    const label = data?.text ?? "";
    const barColor = data?.barColor ?? "#3983eb";
    const chipColor = data?.chipColor ?? barColor;
    const isSummary = Boolean(data?.isSummary);

    return (
        <div className={`pc-bar ${isSummary ? "pc-bar--summary" : ""}`}>
            <span className="pc-bar__fill" style={{ background: barColor }} />
            <span className="pc-bar__label" style={{ background: chipColor }}>
                {label}
            </span>
        </div>
    );
}

function buildFloorModeTasks(data: ProgramChartData): GanttTask[] {
    const out: GanttTask[] = [];

    for (const floor of data.floor_chart ?? []) {
        const floorId = `floor-${floor.floor_id}`;
        const floorName = floor.floor_name ?? `Floor ${floor.floor_id}`;
        const floorColor = floor.floor_color ?? "#6b7280";

        const floorStart = parseISODate(floor.floor_work_date?.start_date);
        const floorEnd = endExclusive(floor.floor_work_date?.end_date);

        out.push({
            id: floorId,
            text: floorName,
            type: "summary",
            start: floorStart ?? undefined,
            end: floorEnd ?? undefined,
            barColor: floorColor,
            chipColor: floorColor,
            isSummary: true,
            open: true,
        } as any);

        (floor.task ?? []).forEach((t, idx) => {
            const taskStart = parseISODate(t.start_date);
            const taskEnd = endExclusive(t.end_date);
            if (!taskStart || !taskEnd) return;

            out.push({
                id: `${floorId}-task-${idx}`,
                parent: floorId,
                text: t.task_name ?? "Task",
                type: "task",
                start: taskStart,
                end: taskEnd,
                barColor: t.color_code ?? "#93c5fd",
                chipColor: t.color_code ?? "#93c5fd",
            });
        });
    }

    return out;
}

function buildTaskModeTasks(data: ProgramChartData): GanttTask[] {
    const out: GanttTask[] = [];

    for (const task of data.task_chart ?? []) {
        const taskId = `task-${task.task_library_id}`;
        const taskName = task.task_name ?? `Task ${task.task_library_id}`;
        const taskColor = task.color_code ?? "#93c5fd";

        const taskStart = parseISODate(task.take_work_date?.start_date);
        const taskEnd = endExclusive(task.take_work_date?.end_date);

        out.push({
            id: taskId,
            text: taskName,
            type: "summary",
            start: taskStart ?? undefined,
            end: taskEnd ?? undefined,
            barColor: taskColor,
            chipColor: taskColor,
            isSummary: true,
            open: true,
        } as any);

        (task.floor ?? []).forEach((f, idx) => {
            const fStart = parseISODate(f.start_date);
            const fEnd = endExclusive(f.end_date);
            if (!fStart || !fEnd) return;

            out.push({
                id: `${taskId}-floor-${idx}`,
                parent: taskId,
                text: f.floor_name ?? "Floor",
                type: "task",
                start: fStart,
                end: fEnd,
                barColor: f.floor_color ?? "#6b7280",
                chipColor: f.floor_color ?? "#6b7280",
            });
        });
    }

    return out;
}


const ProgramChartModal: React.FC<ProgramChartModalProps> = (
    {
        isOpen,
        view,
        onClose,
        programmeChartData,
        projectData,
        loading,
        error
    }
) => {
    const rangeStart = useMemo(() => {
        const firstWeek = programmeChartData?.week_list?.[0]?.week ?? null;
        const base = parseISODate(firstWeek) || parseISODate(programmeChartData?.start_end_date?.start_date ?? null);
        return base ? startOfUKWeek(base) : null;
    }, [programmeChartData]);

    const rangeEnd = useMemo(() => {
        const endBase =
            parseISODate(programmeChartData?.start_end_date?.end_date ?? null) ||
            parseISODate(programmeChartData?.date_list?.[programmeChartData?.date_list?.length - 1]?.date ?? null);

        if (!endBase) return null;

        return startOfNextUKWeek(endBase);
    }, [programmeChartData]);

    const tasks = useMemo<GanttTask[]>(() => {
        if (!programmeChartData) return [];
        return view === "floor"
            ? buildFloorModeTasks(programmeChartData)
            : buildTaskModeTasks(programmeChartData);
    }, [programmeChartData, view]);

    const columns = useMemo(
        () => [
            {
                id: "text",
                header: view === "floor" ? "Floors" : "Tasks",
                flexgrow: 1,
                sort: false,
                resize: false
            },
        ],
        [view]
    );

    const scales = useMemo(
        () => [
            { unit: "week", step: 1, format: "Week %W" },
            { unit: "day", step: 1, format: "%d %D" },
        ],
        []
    );

    const modalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Program data by {view}
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={onClose}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    return (
        <Dialog
            visible={isOpen}
            header={modalHeader}
            headerClassName="custom_modal_header full_view_modal_header with_bg"
            className="custom_modal_dialog modal_dialog_full"
            onHide={onClose}
            contentClassName="full_view_modal_content"
            dismissableMask
        >
            <div className="full_view_modal_body">
                {loading && <PageLoader />}
                {!loading && error && <ErrorPage errorMessage={error ?? "Error"} showGoBackButton={false} />}

                {!loading && !error && (
                    <div className="pc-wrap">
                        <div className="pc-toolbar">
                            <div className="pc-hint">
                                {projectData.job_number} : {projectData.address}
                            </div>

                            <div className="pc-hint">
                                Range: {programmeChartData?.start_end_date?.start_date ?? "-"} → {programmeChartData?.start_end_date?.end_date ?? "-"}
                            </div>
                        </div>

                        <div className="pc-gantt">
                            <Locale words={{ calendar: { weekStart: 1 } }}>
                                <Gantt
                                    readonly={true}
                                    tasks={tasks}
                                    columns={columns}
                                    start={rangeStart ?? undefined}
                                    end={rangeEnd ?? undefined}
                                    scales={scales}
                                    cellWidth={52}
                                    cellHeight={44}
                                    scaleHeight={54}
                                    taskTemplate={ProgramTaskBar}
                                    autoScale={false}
                                    zoom={false}
                                />
                            </Locale>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    )
}

export default ProgramChartModal;